import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdaptiveMathEngine, type MathQuestion } from "./math";
import { gameBus, type CharacterId, type HudSnapshot, type QuestionRequest } from "./game/GameBus";
import { createSaveRepository, type SaveGameV3 } from "./game/core";
import { gameAudio } from "./game/GameAudio";
import { clampLevelNumber, getLevelDefinition, TOTAL_LEVELS, type LevelNumber } from "./game/levels";
import {
  getAccessoryReward,
  validateEquippedAccessoryId,
  type AccessoryId,
} from "./game/wardrobe";
import { CharacterSelect } from "./ui/CharacterSelect";
import { Hud } from "./ui/Hud";
import { PauseModal } from "./ui/PauseModal";
import { QuestionModal, type QuestionDisplay } from "./ui/QuestionModal";
import { ResultScreen } from "./ui/ResultScreen";
import { SettingsModal } from "./ui/SettingsModal";
import { TouchControls } from "./ui/TouchControls";
import { WardrobeScreen } from "./ui/WardrobeScreen";
import { playChime, speakHebrew, stopSpeech } from "./ui/audio";

type AppView = "select" | "playing" | "result" | "wardrobe";
type Feedback = "idle" | "wrong" | "correct";

interface ActiveQuestion {
  boxId: string;
  source: MathQuestion;
  display: QuestionDisplay;
  attempts: number;
}

const EMPTY_HUD: HudSnapshot = { hearts: 3, gems: 0, coins: 0, wisdomStars: 0 };
const GameCanvas = lazy(() => import("./ui/GameCanvas").then((module) => ({ default: module.GameCanvas })));

function visualCount(question: MathQuestion): number | undefined {
  const aid = question.visualAid;
  if (!aid) return undefined;
  if (aid.kind === "objects") return Math.min(20, aid.count);
  if (aid.kind === "object-groups") return Math.min(20, aid.groups[0] + aid.groups[1]);
  if (aid.kind === "object-removal") return Math.min(20, aid.start);
  return undefined;
}

function visualIcon(question: MathQuestion): "gem" | "star" | undefined {
  const aid = question.visualAid;
  if (!aid) return undefined;
  if (aid.kind === "objects" || aid.kind === "object-groups" || aid.kind === "object-removal") return aid.icon;
  return undefined;
}

function visualRemoved(question: MathQuestion): number | undefined {
  return question.visualAid?.kind === "object-removal" ? question.visualAid.removed : undefined;
}

function numberLine(question: MathQuestion): { min: number; max: number; highlighted: number[] } | undefined {
  return question.visualAid?.kind === "number-line"
    ? { min: question.visualAid.min, max: question.visualAid.max, highlighted: [...question.visualAid.highlighted] }
    : undefined;
}

function baseTen(question: MathQuestion): { tens: number; ones: number } | undefined {
  return question.visualAid?.kind === "base-ten" ? { tens: question.visualAid.tens, ones: question.visualAid.ones } : undefined;
}

function toQuestionDisplay(question: MathQuestion): QuestionDisplay {
  return {
    id: question.id,
    prompt: question.promptHebrew,
    expression: question.expressionLTR,
    choices: question.choices.map((choice) => choice.label),
    correctAnswer: String(question.correctAnswer),
    visualCount: visualCount(question),
    visualIcon: visualIcon(question),
    visualRemoved: visualRemoved(question),
    baseTen: baseTen(question),
    numberLine: numberLine(question)
  };
}

function initialSave(repository: ReturnType<typeof createSaveRepository>): SaveGameV3 {
  return repository.load().save;
}

function createQuestionSessionSeed(): string {
  const randomPart = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;
  return `magic-adventures-v2:${randomPart}`;
}

export default function App() {
  const repository = useMemo(() => createSaveRepository(window.localStorage), []);
  const [save, setSave] = useState<SaveGameV3>(() => initialSave(repository));
  const [view, setView] = useState<AppView>("select");
  const [selected, setSelected] = useState<CharacterId>(() => save.selectedCharacterId ?? "dragon");
  const selectedRef = useRef<CharacterId>(selected);
  const [currentLevel, setCurrentLevel] = useState<LevelNumber>(() =>
    clampLevelNumber(save.characterProgress[selected].highestUnlockedLevel)
  );
  const [hud, setHud] = useState<HudSnapshot>(EMPTY_HUD);
  const [resultHud, setResultHud] = useState<HudSnapshot>(EMPTY_HUD);
  const [paused, setPaused] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState<ActiveQuestion | null>(null);
  const [feedback, setFeedback] = useState<Feedback>("idle");
  const [hint, setHint] = useState<string | null>(null);
  const [gameKey, setGameKey] = useState(0);
  const [newlyUnlockedAccessoryId, setNewlyUnlockedAccessoryId] = useState<AccessoryId | null>(null);
  const hintTimerRef = useRef<number | null>(null);
  const speechTimerRef = useRef<number | null>(null);
  const timersRef = useRef<number[]>([]);
  const [mathEngine] = useState(() =>
    new AdaptiveMathEngine({
      seed: createQuestionSessionSeed(),
      initialTier: save.mathProgress.difficulty,
      initialMastery: save.mathProgress.masteryBySkill
    })
  );

  const persist = useCallback((updater: (current: SaveGameV3) => SaveGameV3) => {
    setSave((current) => {
      const next = updater(current);
      repository.save(next);
      return next;
    });
  }, [repository]);

  const updateSettings = useCallback((patch: Partial<SaveGameV3["settings"]>) => {
    persist((current) => ({ ...current, settings: { ...current.settings, ...patch } }));
  }, [persist]);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer));
    timersRef.current = [];
  }, []);

  const stopNarration = useCallback(() => {
    if (speechTimerRef.current !== null) window.clearTimeout(speechTimerRef.current);
    speechTimerRef.current = null;
    stopSpeech();
  }, []);

  const showHint = useCallback((message: string) => {
    setHint(message);
    if (hintTimerRef.current !== null) window.clearTimeout(hintTimerRef.current);
    hintTimerRef.current = window.setTimeout(() => setHint(null), 1800);
  }, []);

  const beginQuestion = useCallback(({ boxId, character, level, questionIndex }: QuestionRequest) => {
    if (level !== currentLevel) return;
    stopNarration();
    const definition = getLevelDefinition(character, level);
    const rotation = definition.questionSkillRotation;
    const skill = rotation[Math.abs(questionIndex) % rotation.length];
    const source = mathEngine.nextQuestion({ skill, minimumTier: definition.mathTierFloor });
    setActiveQuestion({ boxId, source, display: toQuestionDisplay(source), attempts: 0 });
    setFeedback("idle");
    speechTimerRef.current = window.setTimeout(() => {
      speechTimerRef.current = null;
      speakHebrew(source.spokenHebrew, save.settings.muted);
    }, 180);
  }, [currentLevel, mathEngine, save.settings.muted, stopNarration]);

  useEffect(() => {
    const disposers = [
      gameBus.on("hud:update", setHud),
      gameBus.on("question:request", beginQuestion),
      gameBus.on("game:hint", ({ message }) => showHint(message)),
      gameBus.on("game:complete", (snapshot) => {
        setResultHud(snapshot);
        const levelId = `${selected}-${currentLevel}`;
        const firstCompletion = !save.characterProgress[selected].completedLevelIds.includes(levelId);
        const reward = firstCompletion ? getAccessoryReward(selected, currentLevel) : null;
        setNewlyUnlockedAccessoryId(reward?.id ?? null);
        persist((current) => {
          const previous = current.characterProgress[selected];
          const nextUnlockedLevel = clampLevelNumber(currentLevel + 1);
          return {
            ...current,
            selectedCharacterId: selected,
            characterProgress: {
              ...current.characterProgress,
              [selected]: {
                highestUnlockedLevel: Math.max(
                  clampLevelNumber(previous.highestUnlockedLevel),
                  nextUnlockedLevel
                ),
                completedLevelIds: previous.completedLevelIds.includes(levelId)
                  ? previous.completedLevelIds
                  : [...previous.completedLevelIds, levelId],
                equippedAccessoryId: previous.equippedAccessoryId,
                bestCollectibles: {
                  coins: Math.max(previous.bestCollectibles.coins, snapshot.coins),
                  gems: Math.max(previous.bestCollectibles.gems, snapshot.gems),
                  wisdomStars: Math.max(previous.bestCollectibles.wisdomStars, snapshot.wisdomStars),
                  hearts: Math.max(previous.bestCollectibles.hearts, snapshot.hearts)
                }
              }
            }
          };
        });
        const timer = window.setTimeout(() => setView("result"), 450);
        timersRef.current.push(timer);
      })
    ];
    return () => disposers.forEach((dispose) => dispose());
  }, [beginQuestion, currentLevel, persist, save.characterProgress, selected, showHint]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden && view === "playing" && !activeQuestion && !paused) {
        gameBus.emit("game:pause", undefined);
        setPaused(true);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [activeQuestion, paused, view]);

  useEffect(() => () => {
    clearTimers();
    if (hintTimerRef.current !== null) window.clearTimeout(hintTimerRef.current);
    stopNarration();
    gameAudio.dispose();
  }, [clearTimers, stopNarration]);

  useEffect(() => {
    gameAudio.setMuted(save.settings.muted);
    gameAudio.setMusicVolume(save.settings.musicVolume);
    gameAudio.setEffectsVolume(save.settings.effectsVolume);
    gameAudio.requestMusic(selected);
  }, [
    save.settings.effectsVolume,
    save.settings.musicVolume,
    save.settings.muted,
    selected,
  ]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as { __MAGIC_GAME_BUS__?: typeof gameBus }).__MAGIC_GAME_BUS__ = gameBus;
    return () => {
      delete (window as unknown as { __MAGIC_GAME_BUS__?: typeof gameBus }).__MAGIC_GAME_BUS__;
    };
  }, []);

  const launchLevel = useCallback((level: LevelNumber, character: CharacterId = selectedRef.current) => {
    void gameAudio.resumeFromGesture(character);
    clearTimers();
    stopNarration();
    playChime("select", save.settings.muted);
    setHud({ ...EMPTY_HUD });
    setResultHud({ ...EMPTY_HUD });
    setActiveQuestion(null);
    setFeedback("idle");
    setPaused(false);
    setSettingsOpen(false);
    selectedRef.current = character;
    setSelected(character);
    setCurrentLevel(level);
    setGameKey((value) => value + 1);
    setView("playing");
    persist((current) => ({ ...current, selectedCharacterId: character }));
  }, [clearTimers, persist, save.settings.muted, stopNarration]);

  const startGame = useCallback(() => {
    const character = selectedRef.current;
    const level = clampLevelNumber(save.characterProgress[character].highestUnlockedLevel);
    launchLevel(level, character);
  }, [launchLevel, save.characterProgress]);

  const selectCharacter = useCallback((character: CharacterId) => {
    selectedRef.current = character;
    setSelected(character);
    gameAudio.setCharacter(character);
    playChime("select", save.settings.muted);
  }, [save.settings.muted]);

  const exitToSelect = useCallback(() => {
    clearTimers();
    stopNarration();
    setActiveQuestion(null);
    setPaused(false);
    setNewlyUnlockedAccessoryId(null);
    setView("select");
  }, [clearTimers, stopNarration]);

  const replayLevel = useCallback(() => {
    launchLevel(currentLevel);
  }, [currentLevel, launchLevel]);

  const openWardrobe = useCallback(() => {
    setView("wardrobe");
  }, []);

  const continueFromWardrobe = useCallback(() => {
    if (currentLevel >= TOTAL_LEVELS) {
      exitToSelect();
      return;
    }
    setNewlyUnlockedAccessoryId(null);
    launchLevel(clampLevelNumber(currentLevel + 1));
  }, [currentLevel, exitToSelect, launchLevel]);

  const equipAccessory = useCallback((accessoryId: AccessoryId | null) => {
    persist((current) => {
      const previous = current.characterProgress[selected];
      const equippedAccessoryId = validateEquippedAccessoryId(
        selected,
        accessoryId,
        previous.completedLevelIds,
      );
      return {
        ...current,
        characterProgress: {
          ...current.characterProgress,
          [selected]: { ...previous, equippedAccessoryId },
        },
      };
    });
    if (accessoryId) playChime("select", save.settings.muted);
  }, [persist, save.settings.muted, selected]);

  const pauseGame = useCallback(() => {
    gameBus.emit("game:pause", undefined);
    setPaused(true);
  }, []);

  const resumeGame = useCallback(() => {
    gameBus.emit("game:resume", undefined);
    setPaused(false);
  }, []);

  const updateMathSave = useCallback((correct: boolean) => {
    const masteryBySkill = mathEngine.getMastery();
    const highestTier = Math.max(...Object.values(masteryBySkill).map((mastery) => mastery.currentTier));
    persist((current) => ({
      ...current,
      mathProgress: {
        difficulty: Math.min(6, Math.max(current.mathProgress.difficulty, highestTier)) as SaveGameV3["mathProgress"]["difficulty"],
        correctAnswers: current.mathProgress.correctAnswers + (correct ? 1 : 0),
        incorrectAnswers: current.mathProgress.incorrectAnswers + (correct ? 0 : 1),
        recentResults: [...current.mathProgress.recentResults, correct].slice(-5),
        masteryBySkill
      }
    }));
  }, [mathEngine, persist]);

  const chooseAnswer = useCallback((label: string) => {
    if (!activeQuestion || feedback !== "idle") return;
    stopNarration();
    const selectedChoice = activeQuestion.source.choices.find((choice) => choice.label === label);
    if (!selectedChoice) return;
    const answerFeedback = mathEngine.submitAnswer(activeQuestion.source.id, selectedChoice.value);
    updateMathSave(answerFeedback.correct);

    if (!answerFeedback.correct) {
      playChime("wrong", save.settings.muted);
      setFeedback("wrong");
      const timer = window.setTimeout(() => {
        const next = mathEngine.nextQuestion({
          skill: activeQuestion.source.skill,
          minimumTier: getLevelDefinition(selectedRef.current, currentLevel).mathTierFloor
        });
        setActiveQuestion((current) => current ? {
          boxId: current.boxId,
          source: next,
          display: toQuestionDisplay(next),
          attempts: current.attempts + 1
        } : null);
        setFeedback("idle");
        // Spoken encouragement first, then the new question, in one narration
        // so a first-grader who reads slowly still hears the feedback.
        speakHebrew(`כמעט! בואי ננסה יחד. ${next.spokenHebrew}`, save.settings.muted);
      }, 780);
      timersRef.current.push(timer);
      return;
    }

    gameAudio.play("question-solved", selectedRef.current);
    setFeedback("correct");
    speakHebrew("מצוין!", save.settings.muted);
    const boxId = activeQuestion.boxId;
    const resumeTimer = window.setTimeout(() => {
      gameBus.emit("question:resolved", { boxId });
      setActiveQuestion(null);
      setFeedback("idle");
    }, 680);
    timersRef.current.push(resumeTimer);
  }, [activeQuestion, currentLevel, feedback, mathEngine, save.settings.muted, stopNarration, updateMathSave]);

  const toggleMute = useCallback(() => {
    const muted = !save.settings.muted;
    if (muted) stopNarration();
    gameAudio.setMuted(muted);
    updateSettings({ muted });
    gameBus.emit("audio:mute", { muted });
  }, [save.settings.muted, stopNarration, updateSettings]);

  const toggleReducedMotion = useCallback(() => {
    const reducedMotion = !save.settings.reducedMotion;
    updateSettings({ reducedMotion });
    gameBus.emit("motion:reduced", { reduced: reducedMotion });
  }, [save.settings.reducedMotion, updateSettings]);

  const toggleHighContrast = useCallback(() => {
    updateSettings({ highContrast: !save.settings.highContrast });
  }, [save.settings.highContrast, updateSettings]);

  const changeMusicVolume = useCallback((musicVolume: number) => {
    updateSettings({ musicVolume });
  }, [updateSettings]);

  const changeEffectsVolume = useCallback((effectsVolume: number) => {
    updateSettings({ effectsVolume });
  }, [updateSettings]);

  const levelDefinition = useMemo(
    () => getLevelDefinition(selected, currentLevel),
    [currentLevel, selected]
  );
  const levelByCharacter = useMemo<Record<CharacterId, LevelNumber>>(() => ({
    dragon: clampLevelNumber(save.characterProgress.dragon.highestUnlockedLevel),
    unicorn: clampLevelNumber(save.characterProgress.unicorn.highestUnlockedLevel)
  }), [
    save.characterProgress.dragon.highestUnlockedLevel,
    save.characterProgress.unicorn.highestUnlockedLevel
  ]);
  const accessoryByCharacter = useMemo<Record<CharacterId, AccessoryId | null>>(() => ({
    dragon: save.characterProgress.dragon.equippedAccessoryId,
    unicorn: save.characterProgress.unicorn.equippedAccessoryId,
  }), [
    save.characterProgress.dragon.equippedAccessoryId,
    save.characterProgress.unicorn.equippedAccessoryId,
  ]);
  const collectibleTotals = useMemo(() => ({
    coins: levelDefinition.collectibles.filter((item) => item.kind === "coin").length,
    gems: levelDefinition.collectibles.filter((item) => item.kind === "gem").length
  }), [levelDefinition]);

  return (
    <div className={save.settings.highContrast ? "app high-contrast" : "app"}>
      {view === "select" ? (
        <CharacterSelect
          selected={selected}
          muted={save.settings.muted}
          levelByCharacter={levelByCharacter}
          accessoryByCharacter={accessoryByCharacter}
          totalLevels={TOTAL_LEVELS}
          onSelect={selectCharacter}
          onStart={startGame}
          onToggleMute={toggleMute}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      ) : null}

      {view === "playing" ? (
        <main className="game-screen">
          <Suspense fallback={<div className="game-loading" role="status">פותחים את עולם הקסם…</div>}>
            <GameCanvas
              key={`${selected}-${currentLevel}-${accessoryByCharacter[selected] ?? "none"}-${gameKey}`}
              character={selected}
              level={currentLevel}
              accessoryId={accessoryByCharacter[selected]}
              reducedMotion={save.settings.reducedMotion}
              muted={save.settings.muted}
            />
          </Suspense>
          <Hud
            hud={hud}
            character={selected}
            level={currentLevel}
            totalLevels={TOTAL_LEVELS}
            levelName={levelDefinition.displayName}
            coinTotal={collectibleTotals.coins}
            gemTotal={collectibleTotals.gems}
            wisdomStarTotal={levelDefinition.questionBoxes.length}
            onPause={pauseGame}
          />
          <TouchControls character={selected} disabled={paused || activeQuestion !== null} />
          {hint ? <div className="hint-toast" role="status">{hint}</div> : null}
          {activeQuestion ? (
            <QuestionModal
              question={activeQuestion.display}
              feedback={feedback}
              attempts={activeQuestion.attempts}
              onChoose={chooseAnswer}
              onSpeak={() => {
                stopNarration();
                speakHebrew(activeQuestion.source.spokenHebrew, save.settings.muted);
              }}
            />
          ) : null}
          {paused && !activeQuestion ? (
            <PauseModal
              muted={save.settings.muted}
              reducedMotion={save.settings.reducedMotion}
              onResume={resumeGame}
              onRestart={replayLevel}
              onExit={exitToSelect}
              onToggleMute={toggleMute}
              onToggleReducedMotion={toggleReducedMotion}
            />
          ) : null}
          <div className="rotate-hint">כדי לראות את כל עולם הקסם, סובבי את המכשיר לרוחב ✨</div>
        </main>
      ) : null}

      {view === "result" ? (
        <ResultScreen
          character={selected}
          hud={resultHud}
          level={currentLevel}
          totalLevels={TOTAL_LEVELS}
          levelName={levelDefinition.displayName}
          gemTotal={collectibleTotals.gems}
          wisdomStarTotal={levelDefinition.questionBoxes.length}
          equippedAccessoryId={accessoryByCharacter[selected]}
          newlyUnlockedAccessoryId={newlyUnlockedAccessoryId}
          onReplay={replayLevel}
          onOpenWardrobe={openWardrobe}
        />
      ) : null}

      {view === "wardrobe" ? (
        <WardrobeScreen
          character={selected}
          level={currentLevel}
          totalLevels={TOTAL_LEVELS}
          completedLevelIds={save.characterProgress[selected].completedLevelIds}
          equippedAccessoryId={accessoryByCharacter[selected]}
          newlyUnlockedAccessoryId={newlyUnlockedAccessoryId}
          onEquip={equipAccessory}
          onBack={() => setView("result")}
          onContinue={continueFromWardrobe}
        />
      ) : null}

      {settingsOpen ? (
        <SettingsModal
          muted={save.settings.muted}
          reducedMotion={save.settings.reducedMotion}
          highContrast={save.settings.highContrast}
          musicVolume={save.settings.musicVolume}
          effectsVolume={save.settings.effectsVolume}
          onClose={() => setSettingsOpen(false)}
          onToggleMute={toggleMute}
          onToggleReducedMotion={toggleReducedMotion}
          onToggleHighContrast={toggleHighContrast}
          onMusicVolume={changeMusicVolume}
          onEffectsVolume={changeEffectsVolume}
        />
      ) : null}
    </div>
  );
}
